<template>
	<div>
		<div class="flex border-t border-gray-200 rounded-br-lg hidden md:flex">
			<!-- Label -->
			<div class="p-3 pl-6 flex-grow flex items-center gap-x-2 rounded-bl-lg w-[20%] min-w-20 sm:max-w-28 md:min-w-24 md:max-w-[15%]">
				<span class="max-w-full truncate">{{ name }}</span>
			</div>

			<!-- Status -->
			<div class="p-3 text-center flex flex-grow py-[2px] w-[35%] min-w-24 md:min-w-24 sm:max-w-[20%] ">
				<div class="relative flex w-[80%] items-center">
					<div
						v-if="progress > 0"
						class="h-full bg-green-200"
						:style="`width: ${progress}%`"
					></div>
					<div v-if="progress < 1" class="bg-red-400 w-1 h-full flex-shrink-0"></div>
					<div class="absolute right-2">{{ progress }}%</div>
				</div>

				<div class="flex flex-grow justify-center items-center pl-2">
					<font-awesome-icon
						v-if="progress >= 95 && progress <= 105"
						icon="fas fa-check-square"
						class="text-xl text-green"
					/>

					<ui-tooltip
						v-else-if="progress > 105"
						class="font-normal"
						text="Achtung! Ihre verkaufte Erntemenge ist größer als Ihre geplante Erntemenge!"
					>
						<template v-slot:label>
							<ui-icon
								name="alert-triangle"
								class="w-5 h-5 text-red-600"
								stroke-width="2"
							/>
						</template>
					</ui-tooltip>
				</div>
			</div>

			<!-- Gesamt -->
			<div class="p-3 text-left flex items-center basis-1/12 flex-grow bg-gray-100 sm:bg-transparent min-w-22 sm:min-w-24 sm:max-w-[11%] pl-3 lg:pl-5">
				{{ formatNumber(qty__mt, true) }} t
			</div>

			<!-- Verkauft -->
			<div class="p-3 text-right basis-1/12 flex items-center flex-grow hidden sm:flex sm:min-w-22 md:min-w-22 sm:max-w-[11%]">
      <span v-if="aggregates.qty__mt > 0">
        {{ formatNumber(aggregates.qty__mt, true) }} t
      </span>
				<span v-else>-</span>
			</div>

			<!-- Offen -->
			<div class="p-3 text-right basis-1/12 flex items-center flex-grow hidden sm:flex sm:min-w-22 md:min-w-22 sm:max-w-[11%] font-semibold">
      <span v-if="qty__mt - aggregates.qty__mt > 0">
        {{ formatNumber((qty__mt - aggregates.qty__mt), true) }} t
      </span>
				<span v-else>-</span>
			</div>

			<!-- Ø Preis -->
			<div class="p-3 text-center basis-1/12 flex flex-grow items-center hidden sm:flex sm:min-w-24 sm:max-w-[15%]">
      <span v-if="aggregates.qty__mt > 0">
        {{ formatNumber(avgPrice) }} €/t
      </span>
				<span v-else>-</span>
			</div>

			<!-- Best Price -->
			<div class="p-3 text-center basis-1/12 flex-grow flex items-center hidden sm:flex sm:min-w-24 md:min-w-28 sm:max-w-[15%]">
				<button
					class="cursor-pointer underline text-orange-500 underline-offset-4 font-semibold"
					:disabled="disabled"
					v-if="best_price && progress < 100 && this.acl['sale_intentions.create'] && is_main"
					v-on:click="emitBidModal"
				>
					{{ formatNumber(best_price.price_estimate, false, 'de-DE', 2) }} €/t
				</button>

				<button
					class="mx-2 cursor-pointer underline text-orange-500 underline-offset-4 font-semibold"
					v-else-if="best_price && !this.acl['sale_intentions.create'] && is_main"
					v-on:click="
						$bus.$emit('modal:open', {
							view: {
								view: 'views/Modal/PremiumRequired',
							}
						})
					"
				>
					xxx €/t
				</button>
				<span v-else>-</span>
			</div>

			<!-- Edit -->
			<div class="p-3 flex justify-end bg-gray-100 sm:bg-transparent">

				<ui-tooltip class="font-normal" text="Fruchtart bearbeiten">
					<template v-slot:label>
						<button
							:disabled="disabled"
							v-on:click="emitEditModal"
							class="hover:bg-gray-200 rounded-lg p-2"
							:class="['text-orange-500']"
						>
							<ui-icon class="h-5 w-5" name="edit" stroke-width="2.5" />
						</button>
					</template>
				</ui-tooltip>

				<ui-tooltip class="font-normal" text="Fruchtart löschen">
					<template v-slot:label>
						<button
							v-on:click="emitDeleteModal"
							class="hover:bg-gray-200 rounded-lg p-2 hidden sm:inline-block"
							:class="['text-orange-500']"
						>
							<ui-icon class="h-5 w-5" name="trash" stroke-width="2.5" />
						</button>
					</template>
				</ui-tooltip>

			</div>
		</div>

		<!-- Mobile -->
		<div class="py-3 border-t md:hidden">
			<div class="mb-2">
				<!-- Label -->
				<div class="p-3 py-1 flex-grow flex items-center gap-x-2 rounded-bl-lg justify-between">
					<span class="max-w-full truncate font-semibold">{{ name }}</span>
					<span>
							<button
								:disabled="disabled"
								v-on:click="emitEditModal"
								class="hover:bg-gray-200 rounded-lg p-2"
								:class="['text-orange-500']"
							>
								<ui-icon class="h-5 w-5" name="edit" stroke-width="2.5" />
							</button>

							<button
								v-on:click="emitDeleteModal"
								class="hover:bg-gray-200 rounded-lg p-2"
								:class="['text-orange-500']"
							>
								<ui-icon class="h-5 w-5" name="trash" stroke-width="2.5" />
							</button>
					</span>
				</div>

				<!-- Gesamt -->
				<div class="p-3 py-1 text-left flex items-center basis-1/12 justify-between flex-grow">
					<span>Gesamt:</span>
					<span>{{ formatNumber(qty__mt) }} t</span>
				</div>

				<!-- Verkauft -->
				<div class="p-3 py-1 text-right basis-1/12 flex items-center justify-between flex-grow">
					<span>Verkauft:</span>
					<span v-if="aggregates.qty__mt > 0">
						{{ formatNumber(aggregates.qty__mt) }} t
					</span>
					<span v-else>-</span>
				</div>

				<!-- Offen -->
				<div class="p-3 py-1 text-right basis-1/12 flex items-center justify-between font-semiboldm flex-grow">
					<span>Offen:</span>
					<span v-if="qty__mt - aggregates.qty__mt > 0">
						{{ formatNumber(qty__mt - aggregates.qty__mt) }} t
					</span>
					<span v-else>-</span>
				</div>

				<!-- Ø Preis -->
				<div class="p-3 py-1 text-center basis-1/12 flex flex-grow justify-between items-center">
					<span>Ø-Erlös:</span>
					<span v-if="aggregates.qty__mt > 0">
						 {{ formatNumber(avgPrice) }} €/t
					</span>
					<span v-else>-</span>
				</div>

				<!-- Best Price -->
				<div
					class="p-3 py-1 text-center basis-1/12 flex-grow flex justify-between items-center">
					<span>
						Heute Bestpreis:
					</span>

					<button
						class="cursor-pointer underline text-orange-500 underline-offset-4 font-semibold"
						:disabled="disabled"
						v-if="best_price && progress < 100 && this.acl['sale_intentions.create'] && is_main"
						v-on:click="emitBidModal"
					>
						{{ formatNumber(best_price.price_estimate, true, 'de-DE', 2) }} €/t
					</button>

					<button
						class="mx-2 cursor-pointer underline text-orange-500 underline-offset-4 font-semibold"
						v-else-if="best_price && !this.acl['sale_intentions.create'] && is_main"
						v-on:click="
							$bus.$emit('modal:open', {
								view: {
									view: 'views/Modal/PremiumRequired',
								}
							})
						"
					>
						xxx €/t
					</button>
					<span v-else>-</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	props: {
		disabled: Boolean,
		sale_intention_id: Number,
		name: String,
		is_main: Boolean,
		qty__mt: Number,
		aggregates: Object,
		progress: Number,
		best_price: Object,
	},
	data() {
		return {
			acl: this.$inertia.page.props.acl,
		}
	},
	computed: {
		avgPrice() {
			if (!this.aggregates?.qty__mt) return 0;
			return this.aggregates.price__mt / this.aggregates.qty__mt;
		},
	},
	methods: {
		emitBidModal() {
			this.$bus.$emit(
				'toggle-iframe-modal',
				this.$route('bid.create', {
					targetType: 'BUY',
					targetId: this.best_price.id,
					sourcePriceIndicationId: this.best_price.price_indication_id,
					lastDisplayedPrice: this.formatNumber(this.best_price.price_estimate, false, 'en-US', 2),
				}) + '&iframe_modal=true'
			)
		},
		emitEditModal() {
			this.$bus.$emit('modal:open', {
				view: {
					view: 'views/SaleIntention/Form',
					dataUrl: this.$route('sale_intentions.update', {
						id: this.sale_intention_id,
					}),
				},
				cta: {
					code: 402,
					view: 'views/Modal/PremiumRequired'
				}
			})
		},
		emitDeleteModal() {
			this.$bus.$emit('modal:open', {
				view: {
					view: 'views/SaleIntention/Delete',
					data: { id: this.sale_intention_id }
				},
				size: 'sm'
			})
		},

		formatNumber(number, grouping = false, lang = "de-DE", digits = 0) {
			return new Intl.NumberFormat(lang, {
				useGrouping: grouping,
				minimumFractionDigits: digits,
				maximumFractionDigits: digits,
			}).format(number);
		},
	},
};
</script>
